import * as React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronsUpDown, Check, Search } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { cn, getInitials } from '@/lib/utils';
import type { OrganizationDropdownCardProps } from '@/types';

export function OrganizationDropdownCard({
  organizations,
  activeOrg,
  onSelect,
  roles,
}: OrganizationDropdownCardProps) {
  const [q, setQ] = React.useState('');

  const activeProfile = activeOrg?.client?.client_profile;
  const activeSchema = activeOrg?.client?.schema_name ?? 'Organization';
  const activeOrgType = activeOrg?.client?.organization_type ?? 'Organization';
  const activeName = activeProfile?.name ?? activeSchema;
  const activeLogo = activeProfile?.logo_url ?? '/placeholder.svg';
  const isCompany = (orgType: string) =>
    orgType === 'Company/Business/Enterprise' ||
    orgType === 'NGO/INGO' ||
    orgType === 'City/Municipality';

  const activeId =
    activeOrg?.id ?? activeOrg?.client?.schema_name ?? activeName;

  const filteredOrganizations = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return organizations;

    return organizations.filter((org) => {
      const profile = org.client?.client_profile;
      const schema = (org.client?.schema_name ?? '').toLowerCase();
      const name = (
        profile?.name ??
        org.client?.schema_name ??
        ''
      ).toLowerCase();

      const roleText = (org.user_roles ?? []).join(' ').toLowerCase();

      return (
        name.includes(query) ||
        schema.includes(query) ||
        roleText.includes(query)
      );
    });
  }, [q, organizations]);

  return (
    <div className="group-data-[collapsible=icon]:hidden w-full">
      <DropdownMenu
        onOpenChange={(open) => {
          // clear search when dropdown closes
          if (!open) setQ('');
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'w-full',
              'flex items-center gap-3 rounded-xl bg-sidebar-accent p-3',
              'text-left',
              'hover:bg-sidebar-accent/80 transition-colors'
            )}
          >
            <Avatar className="h-9 w-9">
              <AvatarImage src={activeLogo} alt={activeName} />
              <AvatarFallback>{getInitials(activeName)}</AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{activeName}</p>
              <p className="text-xs uppercase text-muted-foreground truncate">
                {roles?.length
                  ? roles
                      .map((role) => {
                        if (!isCompany(activeOrgType)) return role;

                        switch (role) {
                          case 'SCHOOL ADMIN':
                            return 'COMPANY ADMIN';
                          case 'TEACHER':
                            return 'USER';
                          default:
                            return role;
                        }
                      })
                      .join(', ')
                  : 'NO ROLE'}
              </p>
            </div>

            <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-[320px] p-2">
          <DropdownMenuLabel className="px-2">
            Switch organization
          </DropdownMenuLabel>

          {/* Search input */}
          <div className="px-2 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search organizations..."
                className="pl-9 rounded-xl"
                autoFocus
              />
            </div>
          </div>

          <DropdownMenuSeparator />

          {/* Scrollable list */}
          <div className="max-h-[280px] overflow-auto">
            {filteredOrganizations.length ? (
              filteredOrganizations.map((org) => {
                const profile = org.client?.client_profile;
                const schema = org.client?.schema_name ?? 'Organization';
                const name = profile?.name ?? schema;
                const logo = profile?.logo_url ?? '/placeholder.svg';
                const id = org.id ?? org.client?.schema_name ?? name;
                const isActive = id === activeId;
                const organizationType = org?.client?.organization_type;

                const roles = org?.user_roles?.length
                  ? org.user_roles
                      .map((role) => {
                        if (!isCompany(organizationType)) return role;
                        switch (role) {
                          case 'SCHOOL ADMIN':
                            return 'COMPANY ADMIN';
                          case 'TEACHER':
                            return 'USER';
                          default:
                            return role;
                        }
                      })
                      .join(', ')
                  : 'NO ROLE';

                return (
                  <DropdownMenuItem
                    key={String(id)}
                    onClick={() => onSelect(org)}
                    className="gap-3 rounded-lg"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={logo} alt={name} />
                      <AvatarFallback>{getInitials(name)}</AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {roles}
                      </div>
                    </div>

                    {isActive && <Check className="ml-auto h-4 w-4" />}
                  </DropdownMenuItem>
                );
              })
            ) : (
              <div className="px-3 py-6 text-sm text-muted-foreground">
                No organizations found.
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
